import { useParams, Link } from "react-router-dom";
import { getBlogPosts } from "@/hooks/useBlogStore";
import { useEffect } from "react";
import ReadingProgress from "@/components/base/ReadingProgress";

function formatContent(content: string): string {
  // If content looks like HTML (contains tags), return it directly
  if (/<[a-z][\s\S]*>/i.test(content)) {
    return content;
  }

  // Otherwise treat as markdown-style text
  const lines = content.trim().split('\n');
  let html = '';
  let inTable = false;
  let tableHtml = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Heading
    if (line.startsWith('## ')) {
      if (inTable) {
        html += tableHtml + '</tbody></table></div>';
        inTable = false;
        tableHtml = '';
      }
      html += `<h2 class="text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-3">${line.replace('## ', '')}</h2>`;
      continue;
    }

    // Table separator
    if (line.match(/^\|[-\s|]+\|$/)) {
      continue;
    }

    // Table row
    if (line.startsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableHtml = '<div class="overflow-x-auto my-6"><table class="w-full text-left border-collapse">';
        // Add header from previous line if this is the first data row after header
        const prevLine = lines[i - 1]?.trim();
        if (prevLine && prevLine.startsWith('|')) {
          const headerCells = prevLine.split('|').filter(Boolean).map(c => c.trim());
          tableHtml += '<thead><tr>' + headerCells.map(c => `<th class="px-3 py-2 text-sm font-semibold bg-background-100 border border-background-200 text-foreground-800">${c}</th>`).join('') + '</tr></thead>';
        }
        tableHtml += '<tbody>';
      }
      const cells = line.split('|').filter(Boolean).map(c => c.trim());
      tableHtml += '<tr>' + cells.map(c => `<td class="px-3 py-2 text-sm border border-background-200 text-foreground-700">${c.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</td>`).join('') + '</tr>';
      continue;
    }

    if (inTable) {
      html += tableHtml + '</tbody></table></div>';
      inTable = false;
      tableHtml = '';
    }

    // Bold text
    const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html += `<p class="mb-4 text-foreground-700 leading-relaxed">${formattedLine}</p>`;
  }

  if (inTable) {
    html += tableHtml + '</tbody></table></div>';
  }

  return html;
}

function setMetaTag(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMetaTag(name: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  const el = document.querySelector(`meta[${attr}="${name}"]`);
  if (el) el.remove();
}

function setCanonicalUrl(url: string): void {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

function injectArticleSchema(post: { title: string; excerpt: string; image: string; publishedAt: string; author: string; slug: string; category: string; tags: string[] }): () => void {
  const canonicalUrl = `https://dentalsafetyabroad.com/blog/${post.slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": post.publishedAt,
    "author": {
      "@type": "Organization",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Dental Safety Abroad",
      "url": "https://dentalsafetyabroad.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "url": canonicalUrl,
    "articleSection": post.category,
    "keywords": post.tags.join(', ')
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  script.id = 'article-schema';
  document.head.appendChild(script);

  return () => {
    const el = document.getElementById('article-schema');
    if (el) el.remove();
  };
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const blogPosts = getBlogPosts();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!post) return;

    // Dynamic page title
    document.title = `${post.title} | Dental Safety Abroad`;

    // Dynamic meta tags
    setMetaTag('description', post.excerpt);
    setMetaTag('keywords', post.tags.join(','));
    setMetaTag('og:title', `${post.title} | Dental Safety Abroad`, true);
    setMetaTag('og:description', post.excerpt, true);
    setMetaTag('og:type', 'article', true);
    setMetaTag('og:image', post.image, true);
    setMetaTag('og:url', `https://dentalsafetyabroad.com/blog/${post.slug}`, true);
    setMetaTag('article:published_time', post.publishedAt, true);
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', post.title);
    setMetaTag('twitter:description', post.excerpt);
    setMetaTag('twitter:image', post.image);

    // Last-modified
    let lastModEl = document.querySelector('meta[name="last-modified"]') as HTMLMetaElement | null;
    if (!lastModEl) {
      lastModEl = document.createElement('meta');
      lastModEl.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModEl);
    }
    lastModEl.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Canonical URL
    setCanonicalUrl(`https://dentalsafetyabroad.com/blog/${post.slug}`);

    // Schema.org Article structured data
    const cleanupSchema = injectArticleSchema(post);

    return () => {
      document.title = 'Dental Safety Abroad – Affordable Dental Care You Can Trust';
      setMetaTag('description', 'Your independent guide to safe, affordable dental treatment in Hungary, Poland, Turkey and Spain. Take our free quiz to find your best-fit destination.');
      setMetaTag('keywords', 'dental tourism,dental implants abroad,veneers abroad,Hungary dentist,Poland dentist,Turkey dentist,Spain dentist,affordable dental care');
      removeMetaTag('og:title', true);
      removeMetaTag('og:description', true);
      removeMetaTag('og:type', true);
      removeMetaTag('og:image', true);
      removeMetaTag('og:url', true);
      removeMetaTag('article:published_time', true);
      removeMetaTag('twitter:image');
      cleanupSchema();
    };
  }, [post]);

  if (!post) {
    return (
      <main className="bg-background-50 min-h-screen pt-24 md:pt-28">
        <div className="w-full px-4 md:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-primary-600 hover:text-primary-700 font-medium">
            Back to Blog
          </Link>
        </div>
      </main>
    );
  }

  const related = blogPosts
    .filter((p) => p.slug !== post.slug && p.tags.some((t) => post.tags.includes(t)))
    .slice(0, 3);

  return (
    <main className="bg-background-50 min-h-screen pt-24 md:pt-28">
      <ReadingProgress />
      <article className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-700 transition-colors mb-5 group"
          >
            <span className="w-4 h-4 flex items-center justify-center group-hover:-translate-x-0.5 transition-transform">
              <i className="ri-arrow-left-line"></i>
            </span>
            Back to Blog
          </Link>

          <nav className="text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-primary-700 transition-colors">Home</Link>
              </li>
              <li aria-hidden="true">
                <span className="text-foreground-400">›</span>
              </li>
              <li>
                <Link to="/blog" className="hover:text-primary-700 transition-colors">Blog</Link>
              </li>
              <li aria-hidden="true">
                <span className="text-foreground-400">›</span>
              </li>
              <li>
                <span className="text-foreground-700 font-medium line-clamp-1">{post.title}</span>
              </li>
            </ol>
          </nav>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800">
              {post.category}
            </span>
            <span className="text-xs text-foreground-500">{post.readTime}</span>
            <span className="text-xs text-foreground-400">{post.publishedAt}</span>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
            {post.title}
          </h1>

          <p className="text-sm text-foreground-500 mb-8">
            By <strong>{post.author}</strong>
          </p>

          <div className="w-full h-56 md:h-80 overflow-hidden rounded-xl mb-8">
            <img
              src={post.image}
              alt={post.title}
              title={`${post.title} | Dental Safety Abroad`}
              width={800}
              height={400}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
          </div>

          <div
            className="max-w-none"
            dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
          />

          <div className="mt-8 p-4 md:p-5 bg-background-100/70 border border-background-200 rounded-lg">
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong>Dental Safety Abroad</strong> is your independent guide to safer dental tourism. 
              Visit{" "}
              <a 
                href="https://www.dentalsafetyabroad.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                www.dentalsafetyabroad.com
              </a>
              {" "}for free resources including our destination quiz, red flags guide, and country comparison tool.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-background-200">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-800"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-background-200">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 px-5 py-2.5 rounded-md transition-colors group whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center group-hover:-translate-x-0.5 transition-transform">
                <i className="ri-arrow-left-line"></i>
              </span>
              Back to Blog
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-background-100/70">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
                Keep Reading
              </span>
              <h3 className="text-xl md:text-2xl font-bold text-foreground-950">
                Related Articles
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="block group">
                  <article className="bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300/60 transition-colors h-full flex flex-col">
                    <div className="w-full h-40 overflow-hidden">
                      <img
                        src={r.image}
                        alt={r.title}
                        title={`${r.title} | Dental Safety Abroad`}
                        width={400}
                        height={200}
                        className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-800 whitespace-nowrap">
                          {r.category}
                        </span>
                        <span className="text-xs text-foreground-500">{r.readTime}</span>
                      </div>
                      <h4 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors mb-2">
                        {r.title}
                      </h4>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2 flex-1">
                        {r.excerpt}
                      </p>
                      <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors mt-auto">
                        Read article
                        <span className="w-4 h-4 flex items-center justify-center ml-1">
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}